import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import type { GuildRankCardConfig, GuildRankEffects } from '../types';

export const GUILDCARD_IMG = '/assets/images/rankings/guild';

export const GUILDCARD_THEME: Record<number, string> = {
    1: 'ouro',
    2: 'prata',
    3: 'bronze',
};

const GUILDMARK_URL = '/assets/images/guildmarks/';
const DEFAULT_MARK = `${GUILDMARK_URL}guildmark_999999999.png`;

export function getGuildMarkUrl(guildMarkId?: string): string {
    if (!guildMarkId || guildMarkId === '*') {
        return DEFAULT_MARK;
    }
    return `${GUILDMARK_URL}${guildMarkId}.png`;
}

export interface GuildCardItem {
    rank: number;
    name: string;
    guildMarkId: string;
    grade: number;
    masterName: string;
    exploits: number;
}

type CssStyle = React.CSSProperties;

const centered = (el: { left: number; top: number }): CssStyle => ({
    left: `${el.left}%`,
    top: `${el.top}%`,
});

const SPARKLES: [number, number, number, number, number][] = [
    [8, 20, 22, 0, 1.9],
    [20, 8, 15, 0.6, 2.4],
    [34, 5, 18, 1.1, 1.7],
    [68, 7, 16, 0.3, 2.2],
    [84, 18, 20, 0.9, 1.8],
    [93, 42, 15, 1.5, 2.5],
    [10, 55, 16, 1.8, 2.1],
    [90, 66, 17, 0.2, 2.3],
    [24, 72, 13, 1.3, 2.6],
    [78, 82, 16, 0.7, 1.9],
];

export const GuildRankFX: React.FC<{ fx: GuildRankEffects }> = ({ fx }) => {
    return (
        <>
            {fx.rays && <div className="guild-card-fx-rays" />}
            {fx.glow && <div className="guild-card-fx-glow" />}
            {fx.crown && <img className="guild-card-fx-crown" src={`${GUILDCARD_IMG}/fx/crown.svg`} alt="" />}
            {fx.sparkles && (
                <div className="guild-card-fx-sparkles">
                    {SPARKLES.map(([left, top, size, delay, duration], i) => (
                        <span
                            key={i}
                            className="guild-card-fx-sparkle"
                            style={{
                                left: `${left}%`,
                                top: `${top}%`,
                                ['--s' as string]: `${size}px`,
                                ['--d' as string]: `${duration}s`,
                                ['--dl' as string]: `${delay}s`,
                            }}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

export const GuildRankingCard: React.FC<{ item: GuildCardItem; config: GuildRankCardConfig }> = ({ item, config }) => {
    const { t } = useLanguage();
    const theme = GUILDCARD_THEME[item.rank] ?? 'prata';
    const c = config;
    const isFirst = item.rank === 1;
    const fx = isFirst && c.effects.enabled ? c.effects : null;

    return (
        <div
            className={`guild-card g-rank-${item.rank}${c.highlightFirst ? '' : ' no-highlight'}${fx ? '' : ' no-effects'}`}
            style={{ maxWidth: c.cardMaxWidth }}
        >
            <img className="guild-card-bd" src={`${GUILDCARD_IMG}/guild_bd_${theme}.png`} alt="" />

            {fx && <GuildRankFX fx={fx} />}

            {c.emblem.enabled && (
                <img
                    className="guild-card-emblem"
                    style={{ ...centered(c.emblem), width: `${c.emblem.width}%` }}
                    src={getGuildMarkUrl(item.guildMarkId)}
                    alt=""
                />
            )}

            {c.rankBadge.enabled && (
                <div
                    className="guild-card-rank"
                    style={{ ...centered(c.rankBadge), fontSize: c.rankBadge.fontSize }}
                >
                    #{item.rank}
                </div>
            )}

            {c.placa.enabled && (
                <img
                    className="guild-card-placa"
                    style={{ ...centered(c.placa), width: `${c.placa.width}%` }}
                    src={`${GUILDCARD_IMG}/guild_placa_${theme}.png`}
                    alt=""
                />
            )}

            {c.name.enabled && (
                <div
                    className="guild-card-name"
                    style={{
                        ...centered(c.name),
                        fontSize: c.name.fontSize,
                        color: c.name.color,
                        letterSpacing: c.name.letterSpacing ?? 2,
                    }}
                >
                    {item.name}
                </div>
            )}

            {c.grade.enabled && (
                <div
                    className="guild-card-grade"
                    style={{ ...centered(c.grade), fontSize: c.grade.fontSize, color: c.grade.color }}
                >
                    {t('rankings.grade')}: {item.grade}
                </div>
            )}

            {c.master.enabled && (
                <div
                    className="guild-card-master"
                    style={{ ...centered(c.master), fontSize: c.master.fontSize, color: c.master.color }}
                >
                    {t('rankings.master')}: {item.masterName || '-'}
                </div>
            )}

            {c.exploits.enabled && (
                <div
                    className="guild-card-exploits"
                    style={{ ...centered(c.exploits), fontSize: c.exploits.fontSize, color: c.exploits.color }}
                >
                    {Number(item.exploits).toLocaleString()} {t('rankings.exploits')}
                </div>
            )}
        </div>
    );
};